"use client";

import { Button } from "@/components/actions/Button";
import { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

export default function Hero({ title, link }: { title: string; link: string }) {
  const centerRef = useRef<SVGSVGElement>(null);
  const upperRightRef = useRef<SVGSVGElement>(null);
  const bottomRef = useRef<SVGSVGElement>(null);
  const leftRef = useRef<SVGSVGElement>(null);
  const headerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);

    // Set initial states
    gsap.set(centerRef.current, { opacity: 0 });
    gsap.set(upperRightRef.current, { opacity: 0 });
    gsap.set(bottomRef.current, { opacity: 0 });
    gsap.set(leftRef.current, { opacity: 0 });

    // Center image fades in on load
    gsap.to(centerRef.current, {
      opacity: 1,
      duration: 1,
      ease: "power2.out",
    });

    // Pin the header on scroll
    // ScrollTrigger.create({
    //   trigger: headerRef.current,
    //   start: "top top",
    //   end: "+=200vh",

    // });

    // Sequential fade-in animations on scroll with overlap
    const scrollTl = gsap.timeline({
      scrollTrigger: {
        trigger: headerRef.current,
        start: "top top",
        end: "+=150vh",
        scrub: 1,
        pin: true,
      },
    });

    scrollTl
      .to(
        bottomRef.current,
        {
          opacity: 1,
          duration: 1,
          ease: "power2.out",
        },
        0.3
      )
      .to(
        leftRef.current,
        {
          opacity: 1,
          duration: 1,
          ease: "power2.out",
        },
        0.6
      )
      .to(
        upperRightRef.current,
        {
          opacity: 1,
          duration: 1,
          ease: "power2.out",
        },
        0.9
      );

    // Store ScrollTrigger instances for proper cleanup
    const triggers = ScrollTrigger.getAll();

    return () => {
      // Kill only the triggers created in this component
      triggers.forEach((trigger) => trigger.kill());
    };
  }, []);

  return (
    <header
      ref={headerRef}
      className="min-h-[909px] lg:h-[100dvh] as-hero flex justify-center items-center pt-[213px] px-4 lg:px-0"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 max-w-full w-[1100px] mx-auto items-center">
        <div className="stack gap-6">
          <p className="text-[2rem] md:text-[2.375rem] leading-[1.25] text-dark-blue font-medium text-balance">
            Assisted Living at {title}
          </p>
          <h1 className="text-[3rem] md:text-[4.5rem] text-dark-blue z-10 leading-none truth-font  text-balance">
            A little help is available <i>here</i>
          </h1>
          <Button
            onClick={() => {
              document.getElementById("contact")?.scrollIntoView({
                behavior: "smooth",
              });
            }}
            className="w-max"
          >
            <span>Connect with an Advisor</span>
          </Button>
        </div>
        <div>
          <svg
            width="545"
            height="591"
            viewBox="0 0 545 591"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            xmlnsXlink="http://www.w3.org/1999/xlink"
            className="max-w-full"
          >
            <mask
              id="mask0_81_386"
              style={{ maskType: "alpha" }}
              maskUnits="userSpaceOnUse"
              x="170"
              y="83"
              width="289"
              height="404"
            >
              <rect
                x="170.547"
                y="83.2578"
                width="287.537"
                height="403.018"
                fill="#D9D9D9"
              />
            </mask>
            <g mask="url(#mask0_81_386)">
              <image
                id="image0_81_386"
                x="170.547"
                y="54.9688"
                width="287.537"
                height="431.306"
                preserveAspectRatio="xMidYMid meet"
                xlinkHref="/a-little-help-hero-center.jpg"
                ref={centerRef}
              />
              {/* <rect
                x="170.547"
                y="54.9688"
                width="287.537"
                height="431.306"
                fill="url(#pattern0_81_386)"
              /> */}
            </g>

            {/* <rect
              x="384.57"
              y="0.917969"
              width="160.068"
              height="122.812"
              fill="url(#pattern1_81_386)"
            /> */}
            <image
              id="image1_81_386"
              x="384.57"
              y="0.917969"
              width="160.068"
              height="122.812"
              preserveAspectRatio="xMidYMid meet"
              xlinkHref="/a-little-help-hero-upper-right.jpg"
              className="opacity-0"
              ref={upperRightRef}
            />
            <mask
              id="mask1_81_386"
              style={{ maskType: "luminance" }}
              maskUnits="userSpaceOnUse"
              x="348"
              y="352"
              width="182"
              height="239"
            >
              <path
                d="M529.799 352.889H348.609V590.997H529.799V352.889Z"
                fill="white"
              />
            </mask>
            <g>
              <image
                id="image2_81_386"
                x="245.398"
                y="318.875"
                width="452.81"
                height="297.345"
                preserveAspectRatio="xMidYMid"
                xlinkHref="/a-little-help-hero-bottom.jpg"
                className="opacity-0"
                ref={bottomRef}
              />
              {/* <rect
                x="245.398"
                y="318.875"
                width="452.81"
                height="297.345"
                fill="url(#image2_81_386)"
              /> */}
            </g>
            <image
              id="image3_81_386"
              x="0.3125"
              y="237.871"
              width="219.211"
              height="146.141"
              preserveAspectRatio="xMidYMid meet"
              xlinkHref="/a-little-help-hero-left.jpg"
              className="opacity-0"
              ref={leftRef}
            />
            {/* <rect
              x="0.3125"
              y="237.871"
              width="219.211"
              height="146.141"
              fill="url(#pattern3_81_386)"
            /> */}
            <defs>
              <pattern
                id="pattern0_81_386"
                patternContentUnits="objectBoundingBox"
                width="1"
                height="1"
              >
                <use
                  xlinkHref="#image0_81_386"
                  transform="scale(0.00125 0.000833333)"
                />
              </pattern>
              <pattern
                id="pattern1_81_386"
                patternContentUnits="objectBoundingBox"
                width="1"
                height="1"
              >
                <use
                  xlinkHref="#image1_81_386"
                  transform="matrix(0.00123956 0 0 0.00161559 -0.172489 -0.102621)"
                />
              </pattern>
              <pattern
                id="pattern2_81_386"
                patternContentUnits="objectBoundingBox"
                width="1"
                height="1"
              >
                <use
                  xlinkHref="#image2_81_386"
                  transform="scale(0.000833333 0.00126904)"
                />
              </pattern>
              <pattern
                id="pattern3_81_386"
                patternContentUnits="objectBoundingBox"
                width="1"
                height="1"
              >
                <use
                  xlinkHref="#image3_81_386"
                  transform="scale(0.000833333 0.00125)"
                />
              </pattern>
              <clipPath id="clip0_81_386">
                <rect
                  width="174.848"
                  height="184.231"
                  fill="white"
                  transform="translate(127.406 343.246)"
                />
              </clipPath>
            </defs>
          </svg>
        </div>
      </div>
    </header>
  );
}
